import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * RotatingBorderBox component.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.hasFade=false]
 * @param {string} [props.bgColor="#fff"]
 * @param {string} [props.borderColor="#7D8035"]
 * @returns {JSX.Element}
 */
export default function RotatingBorderBox({ children, hasFade = false, bgColor = "#fff", borderColor = "#7D8035" }) {
  const boxRef = useRef(null);
  const [rgbCode, setRgbCode] = useState({ r: 0, g: 0, b: 0 });

  /** */
  function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace(/^#/, "");

    // Handle shorthand hex (e.g., #FFF)
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
  }

  useEffect(() => {
    setRgbCode(hexToRgb(borderColor));
    const el = boxRef.current;
    if (!el) return;

    const durationMs = 3000;
    const start = performance.now();
    let rafId = null;

    /** */
    function frame(now) {
      const t = hasFade ? Math.min(1, (now - start) / durationMs) : (now % durationMs) / durationMs;
      const angle = 360 * t; // 0deg -> 360deg
      el.style.setProperty("--angle", angle + "deg");

      // keep glow at 1 until 80%, then ease-out to 0 by 100%
      let glow = 1;
      if (hasFade) {
        if (t > 0.8) {
          const f = (t - 0.8) / 0.2; // 0..1 over last 20%
          // ease-out cubic for smoother fade
          const easeOut = 1 - Math.pow(1 - f, 3);
          glow = 1 - easeOut;
        }
        el.style.setProperty("--glow-alpha", glow.toFixed(4));
      } else {
        el.style.setProperty("--glow-alpha", "1");
      }

      if (t < 1) {
        rafId = requestAnimationFrame(frame);
      }
    }

    rafId = requestAnimationFrame(frame);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [borderColor, hasFade]);

  return (
    <div className="grid w-full place-items-center">
      <div
        ref={boxRef}
        className="grid w-full place-items-center rounded-xl border-[4px] border-transparent text-[1.2rem]"
        style={{
          "--border-size": "4px",
          "--angle": "0deg",
          "--glow-alpha": "1",
          backgroundImage: `linear-gradient(to right, ${bgColor}, ${bgColor}), conic-gradient(from var(--angle), transparent 30%, rgba(${rgbCode.r} ${rgbCode.g} ${rgbCode.b} / calc(var(--glow-alpha) * 0.8)), rgba(${rgbCode.r} ${rgbCode.g} ${rgbCode.b} / calc(var(--glow-alpha) * 0.4)), rgba(${rgbCode.r} ${rgbCode.g} ${rgbCode.b} / calc(var(--glow-alpha) * 0.8)), transparent 70%)`,
          backgroundSize: "100% 100%, 100% 100%",
          backgroundPosition: "center, center",
          backgroundClip: "padding-box, border-box",
          backgroundOrigin: "padding-box, border-box",
        }}
      >
        {children}
      </div>
    </div>
  );
}

RotatingBorderBox.propTypes = {
  children: PropTypes.node,
  hasFade: PropTypes.bool,
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
};
